import ButtonGroup from '@material-ui/core/ButtonGroup';
import Popover from '@material-ui/core/Popover';
import { DatePicker } from '@material-ui/pickers';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import CalendarOutlineIcon from '@svg/calendar-outline.svg';
import { isValid } from 'date-fns';
import {
  localEquivalentOfUTC,
  NullableDate,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  getVisibleRangeTitleParts,
  shiftUtcDays,
} from './helpers/complianceCalendarWeekViewHelpers';

const StyledButtonGroup = styled(ButtonGroup)`
  border-radius: 6px;
  outline: 1.5px solid ${({ theme }) => theme.colors.graybbb};
  outline-offset: -1.5px;

  & > .MuiButtonGroup-grouped {
    border: none;

    &:not(:first-child) {
      margin-left: 0;
    }
  }

  & > .MuiButton-root:hover {
    z-index: 1;
    box-shadow: none;
  }

  & > .MuiButton-root:hover:not(:focus-visible) {
    border-radius: 6px;
    outline: 1.5px solid ${({ theme }) => theme.colors.gray999};
    outline-offset: -1.5px;
  }
`;

const StyledDiv = styled.div`
  align-content: center;
  min-width: 0;
  padding: 0 0.5rem;
  color: ${({ theme }) => theme.colors.gray666};
  pointer-events: none;
`;

const LIST_RANGE_DATE_SIDE = {
  FROM: 'from',
  TO: 'to',
} as const;

type ListRangeDateSide =
  typeof LIST_RANGE_DATE_SIDE[keyof typeof LIST_RANGE_DATE_SIDE];

type ListRangeDateButtonProps = {
  exclusiveEnd: Date;
  maxDate: Date;
  onRangeChange: (start: Date, exclusiveEnd: Date) => void;
  start: Date;
};

export function ListRangeDateButton({
  exclusiveEnd,
  maxDate,
  onRangeChange,
  start,
}: ListRangeDateButtonProps) {
  const [openSide, setOpenSide] = useState<ListRangeDateSide | null>(null);
  const fromButtonRef = useRef<HTMLButtonElement>(null);
  const toButtonRef = useRef<HTMLButtonElement>(null);
  const closePicker = () => setOpenSide(null);

  const from = localEquivalentOfUTC(start);
  const to = localEquivalentOfUTC(shiftUtcDays(exclusiveEnd, -1));
  const { startText, endText } = getVisibleRangeTitleParts(start, exclusiveEnd);

  const applyFrom = (value: NullableDate) => {
    if (!value || !isValid(value)) {
      return;
    }

    const nextStart = UTCEquivalentOfLocal(value);
    if (!nextStart) {
      return;
    }

    onRangeChange(
      nextStart,
      nextStart >= exclusiveEnd ? shiftUtcDays(nextStart, 1) : exclusiveEnd,
    );
  };

  const applyTo = (value: NullableDate) => {
    if (!value || !isValid(value)) {
      return;
    }

    const inclusiveEnd = UTCEquivalentOfLocal(value);
    if (!inclusiveEnd) {
      return;
    }

    const nextExclusiveEnd = shiftUtcDays(inclusiveEnd, 1);
    onRangeChange(
      nextExclusiveEnd <= start ? inclusiveEnd : start,
      nextExclusiveEnd,
    );
  };

  return (
    <>
      <StyledButtonGroup variant="outlined" aria-label="Visible date range">
        <Button
          ref={fromButtonRef}
          aria-expanded={openSide === LIST_RANGE_DATE_SIDE.FROM}
          aria-haspopup="dialog"
          aria-label="From date"
          onClick={() =>
            setOpenSide((current) =>
              current === LIST_RANGE_DATE_SIDE.FROM
                ? null
                : LIST_RANGE_DATE_SIDE.FROM,
            )
          }
          startIcon={
            <Tooltip title="Edit date range">
              <span>
                <CalendarOutlineIcon aria-hidden />
              </span>
            </Tooltip>
          }
        >
          {startText}
        </Button>
        <StyledDiv>—</StyledDiv>
        <Button
          ref={toButtonRef}
          aria-expanded={openSide === LIST_RANGE_DATE_SIDE.TO}
          aria-haspopup="dialog"
          aria-label="To date"
          onClick={() =>
            setOpenSide((current) =>
              current === LIST_RANGE_DATE_SIDE.TO
                ? null
                : LIST_RANGE_DATE_SIDE.TO,
            )
          }
        >
          {endText}
        </Button>
      </StyledButtonGroup>

      {/* start date popover */}
      <Popover
        anchorEl={fromButtonRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={closePicker}
        open={openSide === LIST_RANGE_DATE_SIDE.FROM}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <DatePicker
          disableToolbar
          maxDate={to || maxDate}
          onChange={(value) => {
            applyFrom(value);
            closePicker();
          }}
          value={from || null}
          variant="static"
        />
      </Popover>

      {/* end date popover */}
      <Popover
        anchorEl={toButtonRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={closePicker}
        open={openSide === LIST_RANGE_DATE_SIDE.TO}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <DatePicker
          disableToolbar
          maxDate={maxDate}
          minDate={from || undefined}
          onChange={(value) => {
            applyTo(value);
            closePicker();
          }}
          value={to || null}
          variant="static"
        />
      </Popover>
    </>
  );
}
